import { Group } from '__types__/v1/models/Group';
import type { GroupRow } from 'v5/core/resource-groups/groupsDraft';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';

export interface ResourceGroupsMutationService {
  /** Resolves with the group the server created, so a caller can order it. */
  create(
    resourceTypeId: number,
    input: { name: string; colour: string; order: number }
  ): Promise<Group>;
  update(
    id: number,
    patch: Pick<Partial<Group>, 'name' | 'colour'>
  ): Promise<void>;
  archive(id: number): Promise<void>;
  unarchive(id: number): Promise<void>;
  reorder(orderedIds: number[]): Promise<void>;
  /**
   * Writes a draft of a resource type's groups (`saveResourceGroups`),
   * resuming from `progress`; resolves with the new progress and the rows
   * with the ids of the groups it created.
   */
  saveGroups(
    resourceTypeId: number,
    progress: GroupsSaveProgress,
    draft: ReadonlyArray<GroupRow>
  ): Promise<{ progress: GroupsSaveProgress; rows: GroupRow[] }>;
}

/** What a groups Save has written: the groups as saved, and whether created ones still need their order written. */
export type GroupsSaveProgress = {
  saved: GroupRow[];
  orderPending: boolean;
};

/** A groups Save that stopped: what it wrote before `cause`, and the rows with the ids it created. */
export class GroupsSaveError extends Error {
  constructor(
    readonly progress: GroupsSaveProgress,
    readonly rows: GroupRow[],
    readonly cause: unknown
  ) {
    super(getUserFacingErrorMessage(cause));
    this.name = 'GroupsSaveError';
  }
}

export interface WithResourceGroupsMutationService {
  resourceGroupsMutationService: ResourceGroupsMutationService;
}
